/**
 * Display mapping for daemon-ORIGINATED notices (shogunchop ADR-d2bfe).
 *
 * Pure logic in a `.ts` file so the `.tsx` dispatcher and story can import it
 * without breaking Vite Fast Refresh
 * (reference_react_noncomponent_export_breaks_hmr).
 */

import { toast } from '@localagent/ui/components/sonner';
import type { ShogunChopDaemonNotice } from '@localagent/sdk';

export interface DaemonNoticeDisplay {
  title: string;
  description: string;
  tone: 'info' | 'error';
}

export function daemonNoticeDisplay(notice: ShogunChopDaemonNotice): DaemonNoticeDisplay {
  switch (notice.kind) {
    case 'mcp_reconcile_failed':
      return {
        title: 'Could not sync this machine’s MCP servers',
        description: notice.reason,
        tone: 'error',
      };
    case 'mcp_crash': {
      if (!notice.server) {
        throw new Error('daemonNoticeDisplay: the daemon sent an mcp_crash notice without its server');
      }
      return {
        title: `${notice.server.name} stopped unexpectedly`,
        description: notice.reason,
        tone: 'error',
      };
    }
    case 'device_registration_failed':
      return {
        title: 'Could not register this machine',
        description: notice.reason,
        tone: 'error',
      };
    case 'device_registration_refused':
      return {
        title: 'This machine belongs to a different account',
        description: notice.reason,
        tone: 'error',
      };
    case 'mcp_reaped': {
      const plural = notice.count === 1 ? 'server' : 'servers';
      return {
        title: `${notice.count} MCP ${plural} removed`,
        description: notice.reason,
        tone: 'info',
      };
    }
  }
  const unreached: never = notice.kind;
  throw new Error(`daemonNoticeDisplay: the daemon sent an unmapped notice kind ${unreached}`);
}

/**
 * A zero-count reap is a no-op the user never noticed; toasting it would train
 * them to ignore the channel. A failure always shows — its count is 0 by
 * construction.
 */
export function shouldShowDaemonNotice(notice: ShogunChopDaemonNotice): boolean {
  return notice.kind !== 'mcp_reaped' || notice.count > 0;
}

export function showDaemonNoticeToast(notice: ShogunChopDaemonNotice, onRestart?: () => void): void {
  const display = daemonNoticeDisplay(notice);
  const action =
    notice.action === 'restart' && onRestart ? { label: 'Restart', onClick: onRestart } : undefined;
  const classNames = action ? { actionButton: 'pointer-events-auto' } : undefined;
  if (display.tone === 'error') {
    toast.error(display.title, { description: display.description, action, classNames });
  } else {
    toast(display.title, { description: display.description, action, classNames });
  }
}
